import { useState } from "react";
import { typeMeta, CHOICE_TYPES, SCREEN_TYPES, TEXT_TYPES, optionsFor, MEETING_VARS } from "@/lib/forms";
import { Field, TextIn, Toggle } from "@/components/forms/Field";
import ImageInput from "@/components/forms/ImageInput";
import LogicEditor from "@/components/forms/LogicEditor";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BUILTIN_MAP = [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["gmv", "GMV"], ["event_date", "Event date"]];

const SIGN = "\n\n— Thick & Thin";
const MAIL = {
  confirm: ["Booking confirmation", "Right after they book", false, "Confirmed: {{event}} on {{date}} at {{time_short}}",
    "Hi {{first_name}},\n\nYou're booked for {{event}} on {{time}}.\n\nJoin on Google Meet: {{meet_link}}\nNeed to change it? Reschedule or cancel: {{manage_link}}" + SIGN],
  reminder: ["Reminder", "At each reminder time above", true, "Reminder: {{event}} {{when}}",
    "Hi {{first_name}},\n\nA reminder that your Google Meet \"{{event}}\" is {{when}}: {{time}}.\n\nJoin: {{meet_link}}\nNeed to change it? Reschedule or cancel: {{manage_link}}" + SIGN],
  reschedule: ["Rescheduled", "When they move the meeting", false, "Moved: {{event}} is now {{date}} at {{time_short}}",
    "Hi {{first_name}},\n\nYour {{event}} has been moved to {{time}}.\n\nJoin: {{meet_link}}\nChange it again: {{manage_link}}" + SIGN],
  cancel: ["Cancelled", "When they cancel", false, "Cancelled: {{event}} on {{date}}",
    "Hi {{first_name}},\n\nYour {{event}} on {{time}} has been cancelled. Want a new time? Just reply to this email." + SIGN],
};
const MAIL_VARS = ["first_name", "name", "event", "time", "date", "time_short", "when", "meet_link", "manage_link", "host", "old_time", "reason"];

function CalendloEmails({ b, set }) {
  const [kind, setKind] = useState("confirm");
  const [label, hint, defOn, defSubj, defBody] = MAIL[kind];
  const cur = (b.emails || {})[kind] || {};
  const upd = (p) => set({ emails: { ...(b.emails || {}), [kind]: { ...cur, ...p } } });
  const on = cur.enabled ?? defOn;
  return (
    <div className="space-y-2 border-t pt-3" data-testid="calendlo-emails">
      <div className="text-xs font-medium">Emails to the invitee</div>
      <div className="flex flex-wrap gap-1">
        {Object.entries(MAIL).map(([k, [l, , d]]) => {
          const en = ((b.emails || {})[k] || {}).enabled ?? d;
          return <button key={k} type="button" onClick={() => setKind(k)} data-testid={`calendlo-mail-tab-${k}`}
            className={`px-2 py-1 rounded text-xs border ${kind === k ? "border-white" : "border-[var(--border)] text-neutral-400"}`}>{l}{en ? "" : " · off"}</button>;
        })}
      </div>
      <Toggle checked={on} onChange={(v) => upd({ enabled: v })} label={`Send “${label}” — ${hint.toLowerCase()}`} testid={`calendlo-mail-on-${kind}`} />
      <Field label="Subject"><TextIn value={cur.subject ?? defSubj} onChange={(v) => upd({ subject: v })} testid={`calendlo-mail-subject-${kind}`} /></Field>
      <Field label="Body">
        <textarea rows={8} className="w-full px-2.5 py-1.5 rounded border text-sm font-mono text-xs" value={cur.body ?? defBody}
          onChange={(e) => upd({ body: e.target.value })} data-testid={`calendlo-mail-body-${kind}`} />
      </Field>
      <div className="flex flex-wrap items-center gap-1 text-[11px] text-neutral-500">
        <span>Insert:</span>
        {MAIL_VARS.map((v) => <button key={v} type="button" className="px-1.5 py-0.5 rounded bg-[var(--panel-2)] font-mono hover:text-white" onClick={() => upd({ body: `${cur.body ?? defBody}{{${v}}}` })} data-testid={`calendlo-mail-var-${v}`}>{`{{${v}}}`}</button>)}
        <span>or any question id, e.g. {"{{q_first}}"}</span>
      </div>
      {(cur.subject != null || cur.body != null) && <button type="button" className="text-[11px] underline text-neutral-500" onClick={() => upd({ subject: undefined, body: undefined })} data-testid={`calendlo-mail-reset-${kind}`}>Reset to default text</button>}
      <div className="text-[11px] text-neutral-500">Sent from the host's connected Gmail. Google also sends its own calendar invite and updates.</div>
    </div>
  );
}

export function CalendloConfig({ b, set }) {
  const rem = (b.reminders || []).join(", ");
  return (
    <div className="space-y-3 border-t pt-3" data-testid="calendlo-config">
      <Field label="Show first">
        <select className="w-full px-2.5 py-1.5 rounded border text-sm" value={b.display_mode || "overview"} onChange={(e) => set({ display_mode: e.target.value })} data-testid="calendlo-display-mode">
          <option value="overview">Event overview, then calendar</option>
          <option value="calendar">Straight to the calendar</option>
        </select>
      </Field>
      <Field label="Event name"><TextIn value={b.event_name} onChange={(v) => set({ event_name: v })} testid="calendlo-event-name" /></Field>
      <Field label="Event description"><TextIn value={b.event_description} onChange={(v) => set({ event_description: v })} testid="calendlo-event-desc" /></Field>
      <div className="text-[11px] text-neutral-500 -mt-1">Use answers anywhere: {"{{first_name}}"}, {"{{name}}"} or a question id like {"{{q_first}}"} puts in what they typed. Also goes into the Google invite title.</div>
      <Field label="After booking, show"><TextIn value={b.success_text} onChange={(v) => set({ success_text: v })} placeholder="See you at {{time_short}} on {{date}}!" testid="calendlo-success-text" /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Slot every (min)"><TextIn type="number" value={b.slot_interval_min} onChange={(v) => set({ slot_interval_min: v })} testid="calendlo-interval" /></Field>
        <Field label="Buffer between meets (min)"><TextIn type="number" value={b.buffer_min} onChange={(v) => set({ buffer_min: v })} testid="calendlo-buffer" /></Field>
        <Field label="Max meets per host per day (0 = no limit)"><TextIn type="number" value={b.max_per_day} onChange={(v) => set({ max_per_day: v })} testid="calendlo-max-day" /></Field>
        <Field label="Reminder emails (minutes before)"><TextIn value={rem} onChange={(v) => set({ reminders: String(v).split(",").map((x) => parseInt(x.trim(), 10)).filter((x) => x > 0) })} testid="calendlo-reminders" placeholder="1440, 60" /></Field>
      </div>
      <div className="text-[11px] text-neutral-500">Reminders go from the host's connected Gmail with Join, Reschedule and Cancel links. Connect calendars in Settings → Google accounts.</div>
      <CalendloEmails b={b} set={set} />
    </div>
  );
}

export function CalendarConfig({ b, set, mailboxes }) {
  const emails = b.calendar_emails || [];
  const toggleEmail = (e) => set({ calendar_emails: emails.includes(e) ? emails.filter((x) => x !== e) : [...emails, e] });
  const days = b.meeting_days || [];
  return (
    <div className="space-y-3 border-t pt-3" data-testid="calendar-config">
      <Field label="Book on these calendars (round-robin, least busy first)">
        <div className="space-y-1">
          {mailboxes.map((m) => (
            <label key={m.email} className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={emails.includes(m.email)} onChange={() => toggleEmail(m.email)} data-testid={`cal-email-${m.email}`} />
              <span className="font-mono">{m.email}</span>
              <span className={`pill ${m.connected ? "pill-ok" : "pill-warn"}`}>{m.connected ? "connected" : "not connected"}</span>
            </label>
          ))}
        </div>
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Duration (min)"><TextIn type="number" value={b.meeting_duration_min} onChange={(v) => set({ meeting_duration_min: v })} testid="cal-duration" /></Field>
        <Field label="Booking window (days)"><TextIn type="number" value={b.meeting_window_days} onChange={(v) => set({ meeting_window_days: v })} testid="cal-window" /></Field>
        <Field label="From (IST)"><TextIn type="time" value={b.meeting_slot_start} onChange={(v) => set({ meeting_slot_start: v })} testid="cal-start" /></Field>
        <Field label="To (IST)"><TextIn type="time" value={b.meeting_slot_end} onChange={(v) => set({ meeting_slot_end: v })} testid="cal-end" /></Field>
        <Field label="Min notice (hours)"><TextIn type="number" value={b.min_notice_hours} onChange={(v) => set({ min_notice_hours: v })} testid="cal-notice" /></Field>
      </div>
      <Field label="Days">
        <div className="flex flex-wrap gap-1">
          {DAYS.map((d, i) => (
            <button key={d} type="button" data-testid={`cal-day-${i}`}
              onClick={() => set({ meeting_days: days.includes(i) ? days.filter((x) => x !== i) : [...days, i].sort() })}
              className={`px-2 py-1 text-[11px] rounded border ${days.includes(i) ? "bg-white text-black border-white" : "text-neutral-400"}`}>{d}</button>
          ))}
        </div>
      </Field>
      {b.type !== "calendlo" && <>
      <Field label="Event title (lead name is appended)"><TextIn value={b.meeting_title} onChange={(v) => set({ meeting_title: v })} testid="cal-title" /></Field>
      <Field label="Event description">
        <textarea className="w-full px-2.5 py-1.5 rounded border text-sm" rows={3} value={b.meeting_description || ""}
          onChange={(e) => set({ meeting_description: e.target.value })} data-testid="cal-desc" />
      </Field>
      </>}
    </div>
  );
}

function remapOptions(b, next) {
  const old = b.options || [];
  const carry = (m) => {
    if (!m) return m;
    const out = {};
    next.forEach((o, i) => {
      if (!o) return;
      const v = m[o] ?? (old[i] && !next.includes(old[i]) ? m[old[i]] : undefined);
      if (v !== undefined && v !== null && v !== "") out[o] = v;
    });
    return out;
  };
  return { options: next, option_scores: carry(b.option_scores), option_images: carry(b.option_images) };
}

function ChoiceExtras({ b, set }) {
  const opts = optionsFor(b);
  const sc = b.option_scores || {};
  const imgs = b.option_images || {};
  return (
    <div className="space-y-2" data-testid="choice-extras">
      <div className="text-[11px] text-neutral-500">Per-option score{b.type === "picture_choice" ? " & image" : ""} (score feeds logic & the lead's Form score)</div>
      {opts.map((o, i) => (
        <div key={`${i}-${o}`} className="flex items-center gap-2">
          <span className="text-xs flex-1 truncate">{o}</span>
          <input type="number" className="w-16 px-2 py-1 rounded border text-xs" value={sc[o] ?? ""} placeholder="0"
            onChange={(e) => set({ option_scores: { ...sc, [o]: e.target.value === "" ? undefined : Number(e.target.value) } })} data-testid={`opt-score-${i}`} />
          {b.type === "picture_choice" && <div className="w-56"><ImageInput value={imgs[o]} onChange={(v) => set({ option_images: { ...imgs, [o]: v } })} testid={`opt-img-${i}`} /></div>}
        </div>
      ))}
      {b.type !== "picture_choice" && b.type !== "dropdown" && b.type !== "multi_choice" && <Toggle checked={b.allow_other} onChange={(v) => set({ allow_other: v })} label='Add "Other" with free text' testid="allow-other-toggle" />}
      <Toggle checked={b.randomize} onChange={(v) => set({ randomize: v })} label="Randomise option order" testid="randomize-toggle" />
    </div>
  );
}

function ScaleConfig({ b, set }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {b.type === "rating" && <Field label="Stars"><TextIn type="number" value={b.scale_max} onChange={(v) => set({ scale_max: v })} testid="scale-max" /></Field>}
      {b.type === "opinion_scale" && <>
        <Field label="From"><TextIn type="number" value={b.scale_min} onChange={(v) => set({ scale_min: v })} testid="scale-min" /></Field>
        <Field label="To"><TextIn type="number" value={b.scale_max} onChange={(v) => set({ scale_max: v })} testid="scale-max" /></Field>
      </>}
      {b.type !== "rating" && <>
        <Field label="Left label"><TextIn value={b.scale_label_left} onChange={(v) => set({ scale_label_left: v })} testid="scale-left" /></Field>
        <Field label="Right label"><TextIn value={b.scale_label_right} onChange={(v) => set({ scale_label_right: v })} testid="scale-right" /></Field>
      </>}
    </div>
  );
}

export default function BlockConfig({ block: b, onChange, crmFields, mailboxes, blocks, endings, variables, topLevel }) {
  if (!b) return <div className="text-xs text-neutral-600 p-4">Select a block to edit it.</div>;
  const set = (patch) => onChange(b.id, patch);
  const M = typeMeta(b.type);
  const isQ = !SCREEN_TYPES.includes(b.type) && b.type !== "group";
  const custom = (crmFields || []).filter((f) => !f.builtin);
  return (
    <div className="space-y-3" data-testid="block-config">
      <div className="flex items-center gap-2 text-xs text-neutral-400"><M.icon size={13} />{M.label}<span className="ml-auto font-mono text-neutral-600">{b.id}</span></div>
      <Field label={b.type === "group" ? "Group heading" : isQ ? "Question" : "Heading"} hint="Recall an earlier answer with {{question_id}}, e.g. {{q_name}}">
        <TextIn value={b.label} onChange={(v) => set({ label: v })} testid="block-label-input" />
      </Field>
      <Field label="Description (optional)">
        <textarea className="w-full px-2.5 py-1.5 rounded border text-sm" rows={2} value={b.description || ""}
          onChange={(e) => set({ description: e.target.value })} data-testid="block-desc-input" />
      </Field>
      {(blocks || []).some((x) => ["calendlo", "calendar_meet"].includes(x.type)) && (
        <div className="text-[11px] text-neutral-500 flex flex-wrap gap-1 items-center" data-testid="meeting-vars">
          <span>Booked meeting:</span>
          {MEETING_VARS.map((v) => <button key={v} type="button" className="px-1.5 py-0.5 rounded bg-[var(--panel-2)] font-mono hover:text-white" onClick={() => set({ label: `${b.label || ""}{{${v}}}` })} title="Add to heading" data-testid={`meeting-var-${v}`}>{`{{${v}}}`}</button>)}
          <span>click to add to the heading, or paste in the description</span>
        </div>
      )}
      <Field label="Image (optional)"><ImageInput value={b.image_url} onChange={(v) => set({ image_url: v })} testid="block-image" /></Field>
      <Field label={SCREEN_TYPES.includes(b.type) || b.type === "group" ? "Button label" : "Button label (blank = OK / Submit)"}>
        <TextIn value={b.button_label} onChange={(v) => set({ button_label: v })} testid="block-button-input" />
      </Field>
      {(TEXT_TYPES.includes(b.type) || ["dropdown", "date"].includes(b.type)) && (
        <Field label="Placeholder text"><TextIn value={b.placeholder} onChange={(v) => set({ placeholder: v })} testid="block-placeholder-input" /></Field>
      )}
      {b.type === "short_text" && (
        <Field label="Answer type" hint="Locks what people can submit. The form shows an error until the answer matches.">
          <select className="w-full px-2.5 py-1.5 rounded border text-sm" value={b.answer_type || ""} onChange={(e) => set({ answer_type: e.target.value || null })} data-testid="block-answer-type-select">
            <option value="">Any text</option><option value="url">Link (URL)</option><option value="email">Email</option>
            <option value="phone">Phone</option><option value="number">Number</option><option value="letters">Text only (letters)</option>
          </select>
        </Field>
      )}
      {b.type === "consent" && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Accept text"><TextIn value={b.button_accept} onChange={(v) => set({ button_accept: v })} testid="consent-accept" /></Field>
          <Field label="Decline text"><TextIn value={b.button_decline} onChange={(v) => set({ button_decline: v })} testid="consent-decline" /></Field>
        </div>
      )}
      {["rating", "opinion_scale", "nps"].includes(b.type) && <ScaleConfig b={b} set={set} />}
      {CHOICE_TYPES.includes(b.type) && (
        <Field label="Options (one per line)">
          <textarea className="w-full px-2.5 py-1.5 rounded border text-sm font-mono" rows={5}
            value={(b.options || []).join("\n")} onChange={(e) => set(remapOptions(b, e.target.value.split("\n")))}
            onBlur={(e) => set(remapOptions(b, [...new Set(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))]))}
            data-testid="block-options-input" />
        </Field>
      )}
      {(CHOICE_TYPES.includes(b.type) || b.type === "yes_no") && <ChoiceExtras b={b} set={set} />}
      {isQ && <Toggle checked={b.required} onChange={(v) => set({ required: v })} label="Required" testid="block-required-toggle" />}
      {isQ && !["calendar_meet", "calendly", "calendlo"].includes(b.type) && (
        <Field label="Save answer to CRM field" hint="Email / phone are matched automatically. Custom fields come from CRM setup.">
          <select className="w-full px-2.5 py-1.5 rounded border text-sm" value={b.maps_to || ""}
            onChange={(e) => set({ maps_to: e.target.value || null })} data-testid="block-mapsto-select">
            <option value="">— none (stored on response only) —</option>
            {BUILTIN_MAP.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            {custom.map((f) => <option key={f.key} value={f.key}>{f.label} (custom)</option>)}
            {b.maps_to && !BUILTIN_MAP.some(([k]) => k === b.maps_to) && !custom.some((f) => f.key === b.maps_to) &&
              <option value={b.maps_to}>{b.maps_to} (custom)</option>}
          </select>
        </Field>
      )}
      {(b.type === "calendar_meet" || b.type === "calendlo") && <CalendarConfig b={b} set={set} mailboxes={mailboxes} />}
      {b.type === "calendlo" && <CalendloConfig b={b} set={set} />}
      {b.type === "calendly" && (
        <Field label="Calendly link" hint="e.g. https://calendly.com/thickandthin/meet-1. Name and email are prefilled from earlier answers. The booking (slot, meeting type, link) is written onto the lead and starts the SOP chosen on the ending.">
          <TextIn value={b.calendly_url} onChange={(v) => set({ calendly_url: v })} testid="calendly-url-input" />
        </Field>
      )}
      {b.type === "calendly" && (
        <Toggle checked={!!b.calendly_hide_details} onChange={(v) => set({ calendly_hide_details: v })} label="Skip event details, show dates directly" testid="calendly-hide-details-toggle" />
      )}
      {topLevel && <LogicEditor block={b} blocks={blocks} endings={endings} variables={variables} onChange={set} />}
    </div>
  );
}
